import React, { useEffect, useRef } from "react";

// Flat Video View component for regular video display
export function FlatView({ video, overlays }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !video) return;

    let raf = 0;
    let stop = false;

    const updateCanvasSize = () => {
      if (!canvas || !video) return;
      
      const container = canvas.parentElement;
      if (!container) return;
      
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      
      // For full coverage, always set canvas to container dimensions
      canvas.width = containerWidth;
      canvas.height = containerHeight;
      
      // Set CSS dimensions to fill container
      canvas.style.width = '100%';
      canvas.style.height = '100%';
    };

    const draw = () => {
      if (stop) return;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Update canvas size if needed
      updateCanvasSize();

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      if (video.readyState >= 2) { // HAVE_CURRENT_DATA or higher
        try {
          // Calculate scaling to cover entire canvas (like CSS object-fit: cover)
          const videoAspect = video.videoWidth / video.videoHeight;
          const canvasAspect = canvas.width / canvas.height;
          
          let sourceX = 0, sourceY = 0, sourceWidth = video.videoWidth, sourceHeight = video.videoHeight;
          let destX = 0, destY = 0, destWidth = canvas.width, destHeight = canvas.height;
          
          if (canvasAspect > videoAspect) {
            // Canvas is wider - scale video to fit height and crop width
            const scaledWidth = canvas.height * videoAspect;
            destX = (canvas.width - scaledWidth) / 2;
            destWidth = scaledWidth;
          } else {
            // Canvas is taller - scale video to fit width and crop height
            const scaledHeight = canvas.width / videoAspect;
            destY = (canvas.height - scaledHeight) / 2;
            destHeight = scaledHeight;
          }
          
          ctx.drawImage(video, sourceX, sourceY, sourceWidth, sourceHeight, destX, destY, destWidth, destHeight);
        } catch (error) {
          console.warn('Error drawing video frame:', error);
        }
      }

      raf = requestAnimationFrame(draw);
    };

    // Add resize observer to handle container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateCanvasSize();
    });
    
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }

    // Use requestVideoFrameCallback if available for better performance
    if (typeof video.requestVideoFrameCallback === 'function') {
      const videoCallback = (_, __) => {
        if (stop) return;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Update canvas size if needed
        updateCanvasSize();

        // Clear canvas and draw the video frame
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        if (video.readyState >= 2) {
          try {
            // Calculate scaling to cover entire canvas (like CSS object-fit: cover)
            const videoAspect = video.videoWidth / video.videoHeight;
            const canvasAspect = canvas.width / canvas.height;
            
            let sourceX = 0, sourceY = 0, sourceWidth = video.videoWidth, sourceHeight = video.videoHeight;
            let destX = 0, destY = 0, destWidth = canvas.width, destHeight = canvas.height;
            
            if (canvasAspect > videoAspect) {
              // Canvas is wider - scale video to fit height and crop width
              const scaledWidth = canvas.height * videoAspect;
              destX = (canvas.width - scaledWidth) / 2;
              destWidth = scaledWidth;
            } else {
              // Canvas is taller - scale video to fit width and crop height
              const scaledHeight = canvas.width / videoAspect;
              destY = (canvas.height - scaledHeight) / 2;
              destHeight = scaledHeight;
            }
            
            ctx.drawImage(video, sourceX, sourceY, sourceWidth, sourceHeight, destX, destY, destWidth, destHeight);
          } catch (error) {
            console.warn('Error drawing video frame:', error);
          }
        }

        video.requestVideoFrameCallback(videoCallback);
      };
      
      video.requestVideoFrameCallback(videoCallback);
      
      return () => {
        stop = true;
        resizeObserver.disconnect();
      };
    }

    // Fallback to requestAnimationFrame
    raf = requestAnimationFrame(draw);

    return () => {
      stop = true;
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
    };
  }, [video]);

  return (
    <div className="cam360-stage">
      <canvas 
        ref={canvasRef} 
        className="cam360-canvas"
        style={{
          width: '100%',
          height: '100%',
          minHeight: '100%',
          background: '#000',
          display: 'block',
          flex: '1 1 auto',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          objectFit: 'cover'
        }}
      />
      {overlays}
    </div>
  );
}
